import type { ReactNode } from "react";
import styled from "@emotion/styled";

export type SegmentedControlOption<T extends string> = {
  value: T;
  label: ReactNode;
  disabled?: boolean;
};

type SegmentedControlProps<T extends string> = {
  value: T;
  onChange: (value: T) => void;
  options: readonly SegmentedControlOption<T>[];
  /** Names the group for screen readers, e.g. "View mode". */
  "aria-label": string;
};

/**
 * A compact switch between a few ways of looking at the same data, such as
 * "Transactions | Trends". It picks a value; what that value shows is the caller's
 * business and renders wherever it likes on the page.
 *
 * Toggle buttons rather than the underlined Tabs component: a tab has to own a panel, and
 * these own nothing. Each segment says whether it is the current one via aria-pressed, and
 * tabs individually, the way a row of buttons does elsewhere in the app.
 */
export default function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  "aria-label": ariaLabel,
}: SegmentedControlProps<T>) {
  return (
    <Track role="group" aria-label={ariaLabel}>
      {options.map((option) => (
        <Segment
          key={option.value}
          type="button"
          aria-pressed={option.value === value}
          disabled={option.disabled}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </Segment>
      ))}
    </Track>
  );
}

const Track = styled.div(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  height: 32,
  padding: 2,
  boxSizing: "border-box",
  borderRadius: 4,
  backgroundColor: theme.colors.sysBackgroundInactive,
}));

const Segment = styled.button(({ theme }) => ({
  all: "unset",
  display: "inline-flex",
  alignItems: "center",
  height: "100%",
  padding: "0 12px",
  boxSizing: "border-box",
  borderRadius: 2,
  fontFamily: theme.font.fontFamily,
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "22px",
  whiteSpace: "nowrap",
  color: theme.colors.sysTextSecondary,
  cursor: "pointer",
  transition: "background-color 0.2s, color 0.2s",
  "&:hover": {
    color: theme.colors.sysTextDefault,
  },
  "&[aria-pressed='true']": {
    backgroundColor: theme.colors.sysBackgroundDefault,
    color: theme.colors.sysPrimaryDefault,
  },
  "&:focus-visible": {
    outline: `2px solid ${theme.colors.sysPrimarySubtleAlt}`,
  },
  "&:disabled": {
    color: theme.colors.sysTextInactive,
    cursor: "not-allowed",
  },
}));
